/**
 * 液化会话控制器（ADR-0013）。
 *
 * 会话模型对齐 `transform-tool.ts`：菜单"滤镜→液化"进入会话 → 拖笔刷累积
 * 位移场（脏矩形驱动的局部预览，见 `spikes/liquify-preview/` 的性能验证）
 * → 确定提交（wasm 烘焙，一条历史）→ 取消丢弃（什么都不发生）。
 *
 * 不经过 `ToolManager` 挂进常驻工具栏——液化是"滤镜→液化"打开的独立会话
 * （同真实 Photoshop 的液化对话框自带一套工具，不是主画布常驻工具）。
 *
 * 只对当前活动的 raster 图层生效，不支持穿透合成结果——会话期间 TileStore
 * 里的内容就是"液化前的原始像素"（提交前不会有任何写入），预览按需从
 * TileStore 读取，不需要单独维护一份全量快照（`flattenLayerRegion` 复用
 * `engine/liquify/bake.ts` 的同一段读取逻辑）。
 */
import type { LayerId } from '../../engine/tile/key';
import { DisplacementField, padRect, type Point, type Rect } from '../../engine/liquify/field';
import { bakeLiquify, flattenLayerRegion } from '../../engine/liquify/bake';
import { warpRegion } from '../../engine/liquify/warp';
import type { AppStore } from '../../app/store';

export type LiquifyMode = 'push' | 'twirlCw' | 'twirlCcw' | 'pucker' | 'bloat';

export interface LiquifyBrushOptions {
  readonly mode: LiquifyMode;
  /** 文档像素。 */
  readonly radius: number;
  /** 语义因 mode 而异：push 是位移倍率、twirl 是每 stamp 角步长(弧度)、
   * pucker/bloat 是位移比例（内部会按模式转正负号，调用方只传正值）。 */
  readonly strength: number;
}

export interface LiquifyPreviewFrame {
  readonly rect: Rect;
  /** RGBA8，长度 rect.width*rect.height*4。 */
  readonly data: Uint8Array;
}

interface Session {
  readonly layer: LayerId;
  readonly field: DisplacementField;
  last: Point | null;
}

export interface LiquifyDeps {
  readonly store: AppStore;
  readonly requestRender: () => void;
}

export class LiquifyController {
  readonly #deps: LiquifyDeps;
  #session: Session | null = null;

  constructor(deps: LiquifyDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#session !== null;
  }

  get layerId(): LayerId | null {
    return this.#session?.layer ?? null;
  }

  /**
   * 对当前活动 raster 图层开始会话。没有活动图层、活动图层不是 raster、
   * 或图层没有任何已分配的像素块（空图层液化没有意义）时返回 `false`。
   */
  begin(): boolean {
    if (this.#session !== null) return true;
    const store = this.#deps.store;
    const document = store.document;
    if (document === null) return false;
    const layer = store.activeRasterLayerId();
    if (layer === null) return false;
    if (store.tiles.residentTilesOf(layer, 0).next().done === true) return false; // 空图层液化没有意义

    this.#session = {
      layer,
      field: new DisplacementField(document.size.width, document.size.height),
      last: null,
    };
    this.#deps.requestRender();
    return true;
  }

  /** 丢弃会话，不产生任何历史/TileStore 改动。 */
  cancel(): void {
    if (this.#session === null) return;
    this.#session = null;
    this.#deps.requestRender();
  }

  /**
   * 指针路由。`down` 记录起点（不产生位移，推模式需要一个起点才有方向）；
   * `move` 按 `options.mode` 累积位移场，返回本次 stamp 脏矩形的warp后
   * 预览帧（`null` 表示没有新增位移，比如半径为 0）；`up` 清空起点。
   * 会话未激活时返回 `null`。
   */
  async handlePointer(
    phase: 'down' | 'move' | 'up',
    point: Point,
    options: LiquifyBrushOptions,
  ): Promise<LiquifyPreviewFrame | null> {
    const session = this.#session;
    if (session === null) return null;

    if (phase === 'up') {
      session.last = null;
      return null;
    }
    if (phase === 'down') {
      session.last = point;
      return null;
    }

    const from = session.last ?? point;
    this.#stamp(session.field, options, from, point);
    session.last = point;

    const dirty = session.field.dirtyBounds();
    if (dirty === null) return null;
    return await this.previewFrame(dirty);
  }

  #stamp(field: DisplacementField, options: LiquifyBrushOptions, from: Point, to: Point): void {
    const { mode, radius, strength } = options;
    switch (mode) {
      case 'push':
        field.applyPush(from, to, radius, strength);
        return;
      case 'twirlCw':
        field.applyTwirl(to, radius, strength);
        return;
      case 'twirlCcw':
        field.applyTwirl(to, radius, -strength);
        return;
      case 'bloat':
        field.applyPuckerBloat(to, radius, strength);
        return;
      case 'pucker':
        field.applyPuckerBloat(to, radius, -strength);
        return;
    }
  }

  /**
   * 按当前累积的位移场，对文档空间的 `rect` 渲染一帧 warp 后的预览
   * （裁到文档范围内）。供 UI 层在打开会话之初拉一次初始预览，或在
   * `handlePointer` 之外主动重绘任意矩形。
   */
  async previewFrame(rect: Rect): Promise<LiquifyPreviewFrame | null> {
    const session = this.#session;
    const document = this.#deps.store.document;
    if (session === null || document === null) return null;
    // 推模式的位移量不受 radius 约束，矩形边缘常常要读矩形外、但仍在图层
    // 范围内的有效像素——不加 padding 会被误判成"越界"返回透明（黑洞），
    // 同 engine/liquify/bake.ts 的道理
    const margin = Math.ceil(session.field.maxMagnitude()) + 1;
    const bounds = padRect(rect, margin, document.size.width, document.size.height);
    if (bounds.width === 0 || bounds.height === 0) return null;

    const source = flattenLayerRegion(this.#deps.store.tiles, session.layer, bounds);
    const dx = new Float32Array(bounds.width * bounds.height);
    const dy = new Float32Array(bounds.width * bounds.height);
    session.field.flattenTo(dx, dy, bounds);
    const data = await warpRegion(source, bounds.width, bounds.height, dx, dy);
    return { rect: bounds, data };
  }

  /**
   * 提交：空场（用户没有拖动过）等价取消，不产生历史。否则一次性烘焙
   * 提交，一条可 undo 的历史记录。
   */
  async commit(options: { signal?: AbortSignal } = {}): Promise<boolean> {
    const session = this.#session;
    const store = this.#deps.store;
    if (session === null) return false;

    if (session.field.isEmpty()) {
      this.cancel();
      return true;
    }

    const label = '液化';
    const transaction = store.beginPixelTransaction(label);
    const result = await bakeLiquify(store.tiles, session.layer, session.field, transaction, options);
    store.commitPixels(label, transaction.end());
    if (result !== null) store.invalidatePixels(result.dirty);

    this.#session = null;
    this.#deps.requestRender();
    return true;
  }
}
